// src/components/ChatWidget.js
// Admin/User chat widget with cross-region aggregation for admins.

import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { useRegion } from '../context/RegionContext';
import { AuthContext } from '../context/AuthContext';
import {
  fetchChatMessages,
  sendChatMessage,
  markMessagesAsRead,
  fetchChatUsers,
} from '../services/chatApi';
import './ChatWidget.css';

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);

  // Thread state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Compose state
  const [newMessage, setNewMessage] = useState('');
  const [adminSendRegion, setAdminSendRegion] = useState(''); // which region to send into (admin only)

  // Admin state
  const [chatUsers, setChatUsers] = useState([]); // merged across regions
  const [showUserList, setShowUserList] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const { user } = useContext(AuthContext);
  const { selectedRegion, allowedRegions } = useRegion();

  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  // External open trigger (from UI header)
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener('open-chat', open);
    return () => window.removeEventListener('open-chat', open);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  /* =========================
     Helpers (admin aggregation)
     ========================= */

  // Merge user arrays from different regions into a single list keyed by sender_id
  const mergeUsers = useCallback((arraysByRegion) => {
    // arraysByRegion: { regionId: [ {id,name,role,email,unread_count,last_message_at}, ... ] }
    const byId = new Map();

    Object.entries(arraysByRegion).forEach(([region, arr]) => {
      (arr || []).forEach((u) => {
        const key = u.id;
        const prev = byId.get(key);
        const enriched = {
          id: u.id,
          name: u.name,
          role: u.role,
          email: u.email,
          // per-region stats
          regions: prev?.regions || {},
          // aggregate stats
          total_unread: (prev?.total_unread || 0) + (Number(u.unread_count) || 0),
          last_message_at: (() => {
            const prevDate = prev?.last_message_at ? new Date(prev.last_message_at) : null;
            const curDate = u.last_message_at ? new Date(u.last_message_at) : null;
            if (!prevDate) return u.last_message_at || null;
            if (!curDate) return prev.last_message_at || null;
            return curDate > prevDate ? u.last_message_at : prev.last_message_at;
          })(),
        };
        enriched.regions[region] = {
          unread: Number(u.unread_count) || 0,
          last_message_at: u.last_message_at || null,
        };
        byId.set(key, enriched);
      });
    });

    // Sort: unread desc, then last_message desc
    const merged = Array.from(byId.values()).sort((a, b) => {
      if ((b.total_unread || 0) !== (a.total_unread || 0)) {
        return (b.total_unread || 0) - (a.total_unread || 0);
      }
      const da = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const db = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return db - da;
    });

    return merged;
  }, []);

  // Load all users from all allowed regions (admin only)
  const loadChatUsersAllRegions = useCallback(async () => {
    if (!isAdmin) return;

    try {
      const regionList = (allowedRegions && allowedRegions.length > 0)
        ? allowedRegions
        : [selectedRegion].filter(Boolean);

      const results = await Promise.allSettled(
        regionList.map((r) => fetchChatUsers(r))
      );

      const byRegion = {};
      results.forEach((res, idx) => {
        const region = regionList[idx];
        byRegion[region] = res.status === 'fulfilled' && Array.isArray(res.value) ? res.value : [];
      });

      const merged = mergeUsers(byRegion);
      setChatUsers(merged);
    } catch (e) {
      console.error('Error loading all-region chat users:', e);
      setChatUsers([]);
    }
  }, [isAdmin, allowedRegions, selectedRegion, mergeUsers]);

  // Load all messages for a given user across all regions (admin only), merge and sort.
  const loadMessagesAllRegions = useCallback(async (userId) => {
    const regionList = (allowedRegions && allowedRegions.length > 0)
      ? allowedRegions
      : [selectedRegion].filter(Boolean);

    const results = await Promise.allSettled(
      regionList.map((r) => fetchChatMessages(userId, r))
    );

    // Flatten messages from all regions; tag each with its region
    let allMsgs = [];
    let totalUnread = 0;

    results.forEach((res, idx) => {
      const region = regionList[idx];
      if (res.status === 'fulfilled' && res.value) {
        const msgs = Array.isArray(res.value.messages) ? res.value.messages : [];
        const unread = Number(res.value.unreadCount) || 0;
        totalUnread += unread;

        const tagged = msgs.map((m) => ({ ...m, __region: region }));
        allMsgs.push(...tagged);
      }
    });

    // Sort by created_at asc
    allMsgs.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return da - db;
    });

    // Decide default send region (most recent message’s region if present)
    const last = allMsgs[allMsgs.length - 1];
    setAdminSendRegion(last?.__region || (regionList[0] || ''));

    // Mark read in all regions (fire-and-forget)
    await Promise.allSettled(regionList.map((r) => markMessagesAsRead(userId, r)));

    setUnreadCount(0);
    return { allMsgs, totalUnread };
  }, [allowedRegions, selectedRegion]);

  /* =========================
     Load messages (user & admin)
     ========================= */

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);

      // Non-admin: standard single-region thread (server handles “my admin thread” when userId=null)
      if (!isAdmin) {
        const data = await fetchChatMessages(null, selectedRegion);
        setMessages(data?.messages || []);
        setUnreadCount(data?.unreadCount || 0);

        if ((data?.messages?.length || 0) > 0) {
          await markMessagesAsRead(null, selectedRegion);
          setUnreadCount(0);
        }
        return;
      }

      // Admin: must have a selected user to load a thread
      if (isAdmin && selectedUser?.id) {
        const { allMsgs } = await loadMessagesAllRegions(selectedUser.id);
        setMessages(allMsgs);
        return;
      }

      // Admin with no user selected: nothing to load
      setMessages([]);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [isAdmin, selectedUser, selectedRegion, loadMessagesAllRegions, scrollToBottom]);

  /* =========================
     Effects
     ========================= */

  // When widget opens
  useEffect(() => {
    if (!isOpen) return;

    if (isAdmin) {
      loadChatUsersAllRegions();
    }
    if (!isAdmin) {
      loadMessages();
    }
  }, [isOpen, isAdmin, loadChatUsersAllRegions, loadMessages]);

  // When admin selects a user, load their merged thread
  useEffect(() => {
    if (!isOpen || !isAdmin) return;
    if (selectedUser?.id) {
      loadMessages();
    }
  }, [isOpen, isAdmin, selectedUser, loadMessages]);

  // Polling while open
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      if (isAdmin) {
        if (selectedUser?.id) loadMessages();
        loadChatUsersAllRegions(); // refresh list & unread totals
      } else {
        loadMessages();
      }
    }, 10000);
    return () => clearInterval(id);
  }, [isOpen, isAdmin, selectedUser, loadMessages, loadChatUsersAllRegions]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input on open or user change
  useEffect(() => {
    if (isOpen) chatInputRef.current?.focus();
  }, [isOpen, selectedUser]);

  /* =========================
     UI handlers
     ========================= */

  const toggleChat = () => {
    setIsOpen((v) => !v);
    if (!isOpen && unreadCount > 0) setUnreadCount(0); // UI-only
  };

  const handleUserSelect = (u) => {
    setSelectedUser(u);
    setShowUserList(false);
    setMessages([]);
    // adminSendRegion will be set after messages load
  };

  const handleSendMessage = async (e) => {
  e.preventDefault();
  const text = newMessage.trim();
  if (!text) return;

  const sendRegion = isAdmin ? adminSendRegion : selectedRegion;
  if (isAdmin && selectedUser && !sendRegion) {
    alert('Please select a region to send this message into.');
    return;
  }
  const recipientId = isAdmin && selectedUser ? selectedUser.id : null;

  // optimistic
  const tempId = `temp-${Date.now()}`;
  const temp = {
    id: tempId,
    message: text,
    sender_name: user?.name || 'Me',
    sender_role: user?.role || 'user',
    created_at: new Date().toISOString(),
    is_own_message: true,
    __region: sendRegion,
  };
  setMessages(prev => [...prev, temp]);
  setNewMessage('');
  scrollToBottom();

  try {
    const resp = await sendChatMessage({ message: text, recipientId, region: sendRegion });
    const saved = resp?.message; // 👈 from backend change

    if (saved) {
      setMessages(prev =>
        prev.map(m => (m.id === tempId ? { ...saved, __region: sendRegion } : m))
      );
    } else {
      // fallback: reload
      await loadMessages();
    }
  } catch (error) {
    console.error('Error sending message:', error);
    // revert the temp on failure
    setMessages(prev => prev.filter(m => m.id !== tempId));
    alert('Failed to send message. Please try again.');
  }
};

  /* =========================
     Render helpers
     ========================= */

  const formatTimestamp = (timestamp) => {
    const d = new Date(timestamp);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const regionBadge = (r) => (
    <span className="region-badge" title={`Region: ${r}`}>{r}</span>
  );

  // Show per-user short region list (with unread counts) in the admin user list
  const renderUserRegions = (u) => {
    const regions = Object.entries(u.regions || {})
      .sort(([, a], [, b]) => (b.unread || 0) - (a.unread || 0)); // unread first
    return (
      <div className="user-regions">
        {regions.map(([r, info]) => (
          <span key={r} className="user-region-chip" title={`${r} • ${info.unread} unread`}>
            {r}{info.unread ? ` (${info.unread})` : ''}
          </span>
        ))}
      </div>
    );
  };

  const emptyStateText = useMemo(() => {
    if (isAdmin && !selectedUser) return 'Select a user to view messages';
    return 'No messages yet. Start the conversation!';
  }, [isAdmin, selectedUser]);

  // Hide widget if not authenticated
  if (!user) return null;

  return (
    <>
      {/* Floating launcher */}
      <div className={`chat-toggle-btn ${isOpen ? 'open' : ''}`} onClick={toggleChat}>
        <span className="chat-icon">💬</span>
        {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
      </div>

      {/* Panel */}
      {isOpen && (
        <div className="chat-widget">
          <div className="chat-header">
            <div className="chat-title">
              {isAdmin ? (
                <div className="admin-chat-header">
                  <span>Support Chat</span>
                  {selectedUser && (
                    <span className="selected-user"> — {selectedUser.name} ({selectedUser.role})</span>
                  )}
                </div>
              ) : (
                <span>Support Chat</span>
              )}
            </div>
            <div className="chat-controls">
              {isAdmin && (
                <button
                  className="user-list-btn"
                  onClick={() => setShowUserList((s) => !s)}
                  title="Select User"
                >
                  👥
                </button>
              )}
                {/* Open full Messages page in a new tab */}
                <a
                  className="open-full-btn"
                  href={`/messages${isAdmin && selectedUser ? `?uid=${encodeURIComponent(selectedUser.id)}` : ''}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open full view"
                >
                  🗖
                </a>
              <button className="close-btn" onClick={toggleChat}>×</button>
            </div>
          </div>

          {/* Admin: Users list (merged across regions) */}
          {isAdmin && showUserList && (
            <div className="user-list">
              <div className="user-list-header">Select User to Chat With:</div>
              {chatUsers.length === 0 ? (
                <div className="no-users">
                  No users with messages found across your regions.
                </div>
              ) : (
                chatUsers.map((u) => (
                  <div
                    key={u.id}
                    className={`user-item ${selectedUser?.id === u.id ? 'selected' : ''}`}
                    onClick={() => handleUserSelect(u)}
                  >
                    <div className="user-info">
                      <span className="user-name">{u.name}</span>
                      <span className="user-role">({u.role})</span>
                    </div>
                    <div className="user-right">
                      <span className={`user-unread ${u.total_unread ? 'has-unread' : ''}`}>
                        {u.total_unread || 0}
                      </span>
                      {renderUserRegions(u)}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Messages */}
          <div className="chat-messages">
            {loading ? (
              <div className="loading">Loading messages...</div>
            ) : messages.length === 0 ? (
              <>
                <div className="no-messages">{emptyStateText}</div>

                {/* 👇 BIG textarea living in the middle, above Send */}
                <form className="chat-empty-compose" onSubmit={handleSendMessage}>
                  {/* Admin region chooser if needed */}
                  {isAdmin && (
                    <select
                      className="send-region-select"
                      value={adminSendRegion}
                      onChange={(e) => setAdminSendRegion(e.target.value)}
                      title="Choose region to send this message into"
                    >
                      <option value="" disabled>Send to region…</option>
                      {(allowedRegions?.length ? allowedRegions : [selectedRegion])
                        .filter(Boolean)
                        .map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  )}

                  <textarea
                    ref={chatInputRef}
                    className="chat-textarea chat-textarea--big"
                    placeholder="Type your message…"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={loading}
                  />

                  <button
                    type="submit"
                    className="send-btn"
                    disabled={loading || !newMessage.trim() || (isAdmin && !adminSendRegion && selectedUser)}
                  >
                    Send
                  </button>
                </form>
              </>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`message ${m.is_own_message ? 'own' : 'other'}`}>
                  <div className="message-content">
                    <div className="message-text">{m.message}</div>
                    <div className="message-meta">
                      <span className="sender">{m.sender_name} ({m.sender_role})</span>
                      <span className="timestamp">{formatTimestamp(m.created_at)}</span>
                      {m.__region ? regionBadge(m.__region) : null}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>


          {/* Composer (footer) */}
          {messages.length > 0 && (!isAdmin || selectedUser) && (
            <form className="chat-input-form" onSubmit={handleSendMessage}>
              {isAdmin && selectedUser && (
                <select
                  className="send-region-select"
                  value={adminSendRegion}
                  onChange={(e) => setAdminSendRegion(e.target.value)}
                  title="Choose region to send this message into"
                >
                  <option value="" disabled>Send to region…</option>
                  {(allowedRegions?.length ? allowedRegions : [selectedRegion])
                    .filter(Boolean)
                    .map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              )}

              <textarea
                ref={chatInputRef}
                rows={2}
                className="chat-textarea"
                placeholder="Type your message…"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                disabled={loading}
              />

              <button
                type="submit"
                className="send-btn"
                disabled={loading || !newMessage.trim() || (isAdmin && selectedUser && !adminSendRegion)}
              >
                Send
              </button>
            </form>
          )}


        </div>
      )}
    </>
  );
}

export default ChatWidget;
